import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import api from '../services/api';
import { localizeChineseText } from '../services/chineseText';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const PILLARS = [
  ['year', 'yearPillar'],
  ['month', 'monthPillar'],
  ['day', 'dayPillar'],
  ['hour', 'hourPillar']
];

const ELEMENT_KEYS = [
  ['木', 'wood'],
  ['火', 'fire'],
  ['土', 'earth'],
  ['金', 'metal'],
  ['水', 'water']
];

const STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'];
const BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
const STEM_ELEMENTS = {
  甲: '木', 乙: '木', 丙: '火', 丁: '火', 戊: '土', 己: '土', 庚: '金', 辛: '金', 壬: '水', 癸: '水'
};
const BRANCH_ELEMENTS = {
  子: '水', 丑: '土', 寅: '木', 卯: '木', 辰: '土', 巳: '火', 午: '火', 未: '土', 申: '金', 酉: '金', 戌: '土', 亥: '水'
};
const TEN_GOD_SCORE = {
  正官: 8,
  正財: 7,
  偏財: 5,
  食神: 6,
  正印: 4,
  比肩: 1,
  偏印: -2,
  傷官: -4,
  七殺: -5,
  劫財: -7
};

const asText = (value, lang = 'zh-TW') => {
  if (Array.isArray(value)) {
    return value.map((item) => asText(item, lang)).filter(Boolean).join('、');
  }
  if (value && typeof value === 'object') {
    const localized = value[lang] || value['zh-TW'] || value['zh-CN'] || value.en || Object.values(value).find(Boolean) || '';
    return localizeChineseText(localized, lang);
  }
  return value || value === 0 ? localizeChineseText(value, lang) : '';
};

const fillTemplate = (template, replacements) => {
  let output = template || '';
  Object.entries(replacements).forEach(([key, value]) => {
    output = output.replaceAll(`{${key}}`, value || '—');
  });
  return output;
};

const asArray = (value, lang = 'zh-TW') => {
  if (!value && value !== 0) return [];
  const items = Array.isArray(value) ? value : [value];
  return items.map((item) => asText(item, lang)).filter(Boolean);
};

const compactText = (value, lang = 'zh-TW', max = 180) => {
  const text = asText(value, lang).replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
};

const LUCK_DISCLAIMER_PATTERN = /仍須配合原局喜忌和流年[，,]不可單看一柱定吉凶。?/g;
const cleanLuckText = (value, lang = 'zh-TW') => asText(value, lang).replace(LUCK_DISCLAIMER_PATTERN, '').trim();

const buildTenYearPrompt = ({ pillar, lang, t }) => {
  const ageRange = cleanLuckText(pillar.age_range, lang);
  const cycle = cleanLuckText(pillar.pillar, lang);
  const tenGod = cleanLuckText(pillar.ten_god, lang);
  const plain = cleanLuckText(pillar.plain, lang);

  if (lang === 'zh-CN') {
    return `TEN_YEAR_LUCK_EXPLAIN_MORE
请根据已经提供的淵海子平四柱八字、鬼谷子两头钳、袁天罡称骨歌，以及以下十年大运资料，详细解释此人这十年的运程。

指定十年大运：
- 年龄：${ageRange}岁
- 大运：${cycle}
- 十神：${tenGod}
- 原有大运提示：${plain}

请用简体中文，分段、有标题、有条列。必须按年龄阶段给实用建议：如果是儿童/少年，重点讲家庭、学业、性格培养和父母应如何引导；如果是青年，重点讲升学、入行、技能、恋爱和第一份工作；如果是24-34岁或工作年龄，重点讲职业选择、行业方向、创业/打工/进修的取舍、财务与感情；如果是中晚年，重点讲事业收成、家庭责任、健康与资产安排。

请尽量具体，说明适合做什么、不适合做什么、应守还是应攻，并明确连接命盘喜忌、两头钳和称骨歌。不要重复「仍须配合原局喜忌和流年，不可单看一柱定吉凶」。`;
  }

  if (lang === 'en') {
    return `TEN_YEAR_LUCK_EXPLAIN_MORE
Explain this person's selected ten-year luck cycle using the provided Yuanhai Ziping Bazi chart, Guiguzi Liangtoujian reading, Yuan Tiangang bone-weight verse, and this decade's luck data.

Selected decade:
- Age range: ${ageRange}
- Luck cycle: ${cycle}
- Ten god: ${tenGod}
- Existing cycle note: ${plain}

Write in English with clear headings, paragraphs and bullet points. Make the advice age-appropriate: school and family guidance for childhood/teen cycles; study, skills, first job and relationships for young adulthood; career choice, industry direction, business versus employment, further study, money and partnership advice for working-age cycles; family responsibility, health and asset planning for later years. Be specific and connect the advice to the chart's useful/caution elements, Liangtoujian and bone-weight verse.`;
  }

  return `TEN_YEAR_LUCK_EXPLAIN_MORE
請根據已提供的「淵海子平四柱八字」、「鬼谷子兩頭鉗」、「袁天罡稱骨歌」，並結合以下十年大運，詳細解釋此人這十年的運程。

指定十年大運：
- 年齡：${ageRange}歲
- 大運：${cycle}
- 十神：${tenGod}
- 原有大運提示：${plain}

請用${t('plainLanguageName') || '繁體中文'}，分段、有標題、有條列。必須按年齡階段給實用建議：如果是兒童或少年，重點講家庭、學業、性格培養與父母應如何引導；如果是青年，重點講升學、入行、技能、戀愛與第一份工作；如果是24至34歲或工作年齡，重點講職業選擇、行業方向、創業／打工／進修取捨、財務與感情；如果是中晚年，重點講事業收成、家庭責任、健康與資產安排。

請盡量具體，說明適合做甚麼、不適合做甚麼、應守還是應攻，並明確連接命盤喜忌、兩頭鉗與稱骨歌。不要重複「仍須配合原局喜忌和流年，不可單看一柱定吉凶」。`;
};

const hiddenStemHeadline = (hidden, lang = 'zh-TW') => {
  const stems = hidden?.stems || [];
  if (!stems.length) return '—';
  return stems
    .map((item) => [asText(item.stem, lang), asText(item.ten_god, lang)].filter(Boolean).join(' '))
    .filter(Boolean)
    .join('、');
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const getAnnualGanZhi = (year) => {
  const offset = year - 1984;
  const stem = STEMS[((offset % 10) + 10) % 10];
  const branch = BRANCHES[((offset % 12) + 12) % 12];
  return {
    stem,
    branch,
    ganzhi: `${stem}${branch}`,
    elements: [STEM_ELEMENTS[stem], BRANCH_ELEMENTS[branch]].filter(Boolean)
  };
};

const normalizePracticalLang = (lang = 'zh-TW') => {
  if (lang === 'zh-CN') return 'zh-CN';
  if (lang?.startsWith('zh')) return 'zh-TW';
  if (['ko', 'ja', 'ms', 'es'].includes(lang)) return lang;
  return 'en';
};

const PRACTICAL_GUIDANCE = {
  'zh-TW': {
    element: {
      木: { occupation: '教育、顧問、文字策劃、品牌內容、身心健康', area: '學習力、表達力、人脈經營', action: '先累積一張證書、一個作品集，或固定輸出文章／短片。', avoid: '少做短炒和情緒化轉工，先把根基養好。' },
      火: { occupation: '市場推廣、設計、媒體、直播銷售、餐飲美業、產品增長', area: '曝光、說服、舞台感、審美', action: '多做公開展示：簡報、銷售、社群內容，讓人看見你的能力。', avoid: '避免一時衝動答應太多事，先定節奏再衝。' },
      土: { occupation: '地產、工程、營運、項目管理、會計行政、倉儲供應鏈', area: '穩定執行、制度、資產管理', action: '適合把流程、報價、帳目和時間表整理好，靠可靠度賺錢。', avoid: '不要拖延到機會變沉重，也不要替人硬扛所有責任。' },
      金: { occupation: '金融、法律合規、工程技術、數據、安全、醫療牙科、精密工具', area: '專業資格、分析、紀律、標準化', action: '把專業技術磨尖：考牌、學數據工具、做可量化成果。', avoid: '少用太硬的語氣處理人事，規矩要清楚但要留餘地。' },
      水: { occupation: '貿易、物流、旅遊、顧問、研究、語言、跨境業務、產品策略', area: '流動、人脈、資訊整合、跨市場', action: '多接觸外地客戶、平台或新渠道，用資訊差找機會。', avoid: '避免想太多不落地，每週要有可交付成果。' }
    },
    tenGod: {
      正官: '走制度型路線更順，可考慮管理、合規、公職、大公司晉升。',
      七殺: '適合有壓力和競爭的工作，如銷售、營運、保安、創業前線，但要守紀律。',
      正財: '適合穩定收入、客戶管理、財務、營運、地產或實體生意。',
      偏財: '可向生意、渠道、投資研究、商務拓展發展，但先控風險。',
      正印: '適合進修、研究、教育、醫護、顧問，先用學歷或證書加身。',
      偏印: '適合冷門技術、研究、玄學、設計系統、資料分析，靠專長吃飯。',
      食神: '適合內容、教學、餐飲、服務、產品體驗，靠口碑和穩定輸出。',
      傷官: '適合設計、媒體、創意、銷售、科技創新，但要學會同上司合作。',
      比肩: '適合專業合夥、自主品牌、同行網絡，但要分清權責。',
      劫財: '適合競爭性行業和團隊業務，但錢銀合約必須寫清楚。'
    },
    labels: {
      occupation: '職業方向',
      area: '發展重點',
      action: '下一步',
      avoid: '要避開',
      reason: '按命盤喜用與大運氣勢推得，不是空泛建議。'
    }
  },
  'zh-CN': {
    element: {
      木: { occupation: '教育、顾问、文字策划、品牌内容、身心健康', area: '学习力、表达力、人脉经营', action: '先累积一张证书、一个作品集，或固定输出文章/短片。', avoid: '少做短炒和情绪化跳槽，先把根基养好。' },
      火: { occupation: '市场推广、设计、媒体、直播销售、餐饮美业、产品增长', area: '曝光、说服、舞台感、审美', action: '多做公开展示：简报、销售、社群内容，让人看见你的能力。', avoid: '避免一时冲动答应太多事，先定节奏再冲。' },
      土: { occupation: '地产、工程、运营、项目管理、会计行政、仓储供应链', area: '稳定执行、制度、资产管理', action: '适合把流程、报价、账目和时间表整理好，靠可靠度赚钱。', avoid: '不要拖延到机会变沉重，也不要替人硬扛所有责任。' },
      金: { occupation: '金融、法律合规、工程技术、数据、安全、医疗牙科、精密工具', area: '专业资格、分析、纪律、标准化', action: '把专业技术磨尖：考牌、学数据工具、做可量化成果。', avoid: '少用太硬的语气处理人事，规矩要清楚但要留余地。' },
      水: { occupation: '贸易、物流、旅游、顾问、研究、语言、跨境业务、产品策略', area: '流动、人脉、信息整合、跨市场', action: '多接触外地客户、平台或新渠道，用信息差找机会。', avoid: '避免想太多不落地，每周要有可交付成果。' }
    },
    tenGod: {
      正官: '走制度型路线更顺，可考虑管理、合规、公职、大公司晋升。',
      七殺: '适合有压力和竞争的工作，如销售、运营、保安、创业前线，但要守纪律。',
      正財: '适合稳定收入、客户管理、财务、运营、地产或实体生意。',
      偏財: '可向生意、渠道、投资研究、商务拓展发展，但先控风险。',
      正印: '适合进修、研究、教育、医护、顾问，先用学历或证书加身。',
      偏印: '适合冷门技术、研究、玄学、设计系统、数据分析，靠专长吃饭。',
      食神: '适合内容、教学、餐饮、服务、产品体验，靠口碑和稳定输出。',
      傷官: '适合设计、媒体、创意、销售、科技创新，但要学会同上司合作。',
      比肩: '适合专业合伙、自主品牌、同行网络，但要分清权责。',
      劫財: '适合竞争性行业和团队业务，但钱银合同必须写清楚。'
    },
    labels: {
      occupation: '职业方向',
      area: '发展重点',
      action: '下一步',
      avoid: '要避开',
      reason: '按命盘喜用与大运气势推得，不是空泛建议。'
    }
  },
  en: {
    element: {
      木: { occupation: 'education, consulting, writing, brand content, wellness', area: 'learning, communication and network building', action: 'Build one certificate, portfolio, or weekly writing/video output first.', avoid: 'Avoid impulsive job changes or short-term speculation; grow your base first.' },
      火: { occupation: 'marketing, design, media, live sales, beauty/F&B, product growth', area: 'visibility, persuasion, presentation and taste', action: 'Do more public work: pitches, sales, social content, demos.', avoid: 'Do not promise too much too fast; set a rhythm before pushing hard.' },
      土: { occupation: 'property, engineering, operations, project management, accounting/admin, supply chain', area: 'execution, systems and asset management', action: 'Organize process, pricing, accounts and timelines; earn through reliability.', avoid: 'Do not delay until opportunities become heavy, and do not carry everyone’s burden.' },
      金: { occupation: 'finance, law/compliance, engineering, data, security, dentistry/medical tools', area: 'credentials, analysis, discipline and standards', action: 'Sharpen a technical skill: licensing, data tools, measurable results.', avoid: 'Avoid being too harsh with people; keep rules clear but humane.' },
      水: { occupation: 'trade, logistics, travel, consulting, research, languages, cross-border business, product strategy', area: 'mobility, networks, information and new markets', action: 'Meet overseas clients, platforms or channels; use information advantage.', avoid: 'Do not overthink without delivery; ship something useful every week.' }
    },
    labels: {
      occupation: 'Occupation direction',
      area: 'Area to develop',
      action: 'Next step',
      avoid: 'Avoid',
      reason: 'Derived from useful elements and luck-cycle signals, so it stays concrete.'
    }
  },
  ko: {
    element: {
      木: { occupation: '교육, 컨설팅, 글쓰기, 브랜드 콘텐츠, 웰니스', area: '학습력, 표현력, 인맥', action: '자격증 하나, 포트폴리오 하나, 또는 매주 콘텐츠 발행부터 시작하세요.', avoid: '충동적인 이직과 단기 투기는 줄이고 기반을 먼저 키우세요.' },
      火: { occupation: '마케팅, 디자인, 미디어, 라이브 판매, 뷰티/외식, 제품 성장', area: '노출, 설득, 발표력, 감각', action: '프레젠테이션, 판매, SNS 콘텐츠처럼 보이는 일을 늘리세요.', avoid: '한 번에 너무 많이 약속하지 말고 속도를 정한 뒤 밀어붙이세요.' },
      土: { occupation: '부동산, 공정/건설, 운영, 프로젝트 관리, 회계/행정, 공급망', area: '실행력, 시스템, 자산 관리', action: '절차, 견적, 장부, 일정을 정리해 신뢰로 돈을 버는 쪽이 좋습니다.', avoid: '기회를 미루거나 남의 책임을 모두 떠안지 마세요.' },
      金: { occupation: '금융, 법무/컴플라이언스, 엔지니어링, 데이터, 보안, 의료/정밀도구', area: '자격, 분석, 규율, 표준화', action: '자격증, 데이터 도구, 측정 가능한 성과로 전문성을 날카롭게 하세요.', avoid: '사람을 대할 때 너무 강하게 말하지 말고 규칙 속에 여지를 두세요.' },
      水: { occupation: '무역, 물류, 여행, 컨설팅, 리서치, 언어, 해외사업, 제품전략', area: '이동성, 네트워크, 정보, 해외시장', action: '해외 고객, 플랫폼, 새 채널을 만나 정보 차이에서 기회를 찾으세요.', avoid: '생각만 많고 실행이 없지 않게 매주 결과물을 내세요.' }
    },
    labels: { occupation: '직업 방향', area: '키울 영역', action: '다음 행동', avoid: '피할 점', reason: '희용 오행과 대운 흐름에서 뽑은 구체 조언입니다.' }
  },
  ja: {
    element: {
      木: { occupation: '教育、コンサル、文章企画、ブランドコンテンツ、心身ケア', area: '学習力、表現、人脈づくり', action: '資格、ポートフォリオ、または週1回の発信から始めましょう。', avoid: '衝動的な転職や短期投機を避け、まず土台を育てること。' },
      火: { occupation: 'マーケティング、デザイン、メディア、ライブ販売、美容・飲食、プロダクト成長', area: '露出、説得、発表力、センス', action: '提案、販売、SNS発信など、人に見える仕事を増やしましょう。', avoid: '勢いだけで抱え込みすぎず、リズムを決めて進むこと。' },
      土: { occupation: '不動産、建設、運営、プロジェクト管理、会計・事務、物流', area: '実行、仕組み、資産管理', action: '手順、見積、帳簿、予定を整え、信頼で稼ぐ形が合います。', avoid: '先延ばしや、人の責任を全部背負うことは避けましょう。' },
      金: { occupation: '金融、法務・コンプラ、エンジニア、データ、セキュリティ、医療・精密技術', area: '資格、分析、規律、標準化', action: '資格取得、データツール、数値で示せる成果を磨きましょう。', avoid: '人に厳しくなりすぎず、規則に余白を残しましょう。' },
      水: { occupation: '貿易、物流、旅行、コンサル、研究、語学、越境ビジネス、製品戦略', area: '移動、人脈、情報、海外市場', action: '海外顧客、平台、新しい販路に触れ、情報差から機会を探しましょう。', avoid: '考えるだけで終わらず、毎週小さな成果を出すこと。' }
    },
    labels: { occupation: '仕事の方向', area: '伸ばす分野', action: '次の一手', avoid: '避けること', reason: '喜用五行と大運の流れから具体化した助言です。' }
  },
  ms: {
    element: {
      木: { occupation: 'pendidikan, perundingan, penulisan, kandungan jenama, wellness', area: 'belajar, komunikasi dan rangkaian', action: 'Bina satu sijil, portfolio, atau kandungan mingguan dahulu.', avoid: 'Elakkan tukar kerja secara emosi atau spekulasi cepat; kukuhkan asas dahulu.' },
      火: { occupation: 'pemasaran, reka bentuk, media, jualan live, kecantikan/F&B, pertumbuhan produk', area: 'pendedahan, pujukan, pembentangan dan citarasa', action: 'Banyakkan kerja terbuka: pitch, jualan, kandungan sosial, demo.', avoid: 'Jangan janji terlalu banyak; tetapkan rentak sebelum mengejar laju.' },
      土: { occupation: 'hartanah, kejuruteraan, operasi, pengurusan projek, akaun/admin, rantaian bekalan', area: 'pelaksanaan, sistem dan pengurusan aset', action: 'Susun proses, harga, akaun dan jadual; jana wang melalui kebolehpercayaan.', avoid: 'Jangan bertangguh atau memikul semua tanggungjawab orang lain.' },
      金: { occupation: 'kewangan, undang-undang/pematuhan, kejuruteraan, data, keselamatan, alat perubatan', area: 'kelayakan, analisis, disiplin dan piawaian', action: 'Tajamkan kemahiran: lesen, alat data, hasil yang boleh diukur.', avoid: 'Jangan terlalu keras dengan orang; peraturan perlu jelas tetapi berperikemanusiaan.' },
      水: { occupation: 'perdagangan, logistik, pelancongan, perundingan, kajian, bahasa, perniagaan rentas negara, strategi produk', area: 'mobiliti, rangkaian, maklumat dan pasaran baharu', action: 'Jumpa pelanggan luar, platform atau saluran baharu; cari peluang melalui maklumat.', avoid: 'Jangan terlalu banyak berfikir tanpa hasil; siapkan sesuatu setiap minggu.' }
    },
    labels: { occupation: 'Arah pekerjaan', area: 'Bidang perlu dibina', action: 'Langkah seterusnya', avoid: 'Elakkan', reason: 'Dibentuk daripada unsur berguna dan rentak nasib, jadi lebih praktikal.' }
  },
  es: {
    element: {
      木: { occupation: 'educación, consultoría, escritura, contenido de marca, bienestar', area: 'aprendizaje, comunicación y redes', action: 'Construye primero un certificado, portafolio o publicación semanal.', avoid: 'Evita cambios de trabajo impulsivos y especulación rápida; fortalece la base.' },
      火: { occupation: 'marketing, diseño, medios, venta en vivo, belleza/restauración, crecimiento de producto', area: 'visibilidad, persuasión, presentación y gusto', action: 'Haz más trabajo visible: presentaciones, ventas, redes sociales, demos.', avoid: 'No prometas demasiado; fija ritmo antes de acelerar.' },
      土: { occupation: 'inmobiliario, ingeniería, operaciones, gestión de proyectos, contabilidad/admin, cadena de suministro', area: 'ejecución, sistemas y gestión de activos', action: 'Ordena procesos, precios, cuentas y calendario; gana por confiabilidad.', avoid: 'No postergues ni cargues responsabilidades ajenas.' },
      金: { occupation: 'finanzas, legal/compliance, ingeniería, datos, seguridad, herramientas médicas', area: 'credenciales, análisis, disciplina y estándares', action: 'Afila una habilidad: licencia, herramientas de datos, resultados medibles.', avoid: 'No seas demasiado duro con la gente; reglas claras con margen humano.' },
      水: { occupation: 'comercio, logística, viajes, consultoría, investigación, idiomas, negocio internacional, estrategia de producto', area: 'movilidad, redes, información y nuevos mercados', action: 'Busca clientes externos, plataformas o canales nuevos; usa ventaja de información.', avoid: 'No pienses demasiado sin entregar; produce algo útil cada semana.' }
    },
    labels: { occupation: 'Dirección laboral', area: 'Área a desarrollar', action: 'Siguiente paso', avoid: 'Evita', reason: 'Derivado de elementos útiles y ciclos de suerte, para hacerlo concreto.' }
  }
};

const buildPracticalAdvice = ({ usefulElements, cautionElements, luckRows, lang }) => {
  const guidance = PRACTICAL_GUIDANCE[normalizePracticalLang(lang)] || PRACTICAL_GUIDANCE.en;
  const fallback = guidance.element.木;
  const primaryElement = usefulElements.find((element) => guidance.element[element]) || '木';
  const cautionElement = cautionElements.find((element) => guidance.element[element]);
  const elementAdvice = guidance.element[primaryElement] || fallback;
  const cautionAdvice = cautionElement ? guidance.element[cautionElement] : elementAdvice;
  const strongestLuck = [...luckRows].sort((a, b) => (b.score || 0) - (a.score || 0))[0];
  const tenGodNote = guidance.tenGod?.[strongestLuck?.ten_god] || '';
  return {
    labels: guidance.labels,
    reason: guidance.labels.reason,
    occupation: elementAdvice.occupation,
    area: elementAdvice.area,
    action: tenGodNote ? `${elementAdvice.action} ${tenGodNote}` : elementAdvice.action,
    avoid: cautionAdvice.avoid
  };
};

const FortuneResume = ({ data, t, lang = 'zh-TW' }) => {
  const [activeTab, setActiveTab] = useState('original');
  const [luckExplanations, setLuckExplanations] = useState({});
  const [luckLoading, setLuckLoading] = useState({});
  const [luckErrors, setLuckErrors] = useState({});

  if (!data?.bazi) return null;

  const { bazi, ziping, liangtoujian, chenggu } = data;
  const matched = liangtoujian?.matched_combination;
  const matchingStar = liangtoujian?.stars?.find((star) =>
    star.combinations?.some((combination) => combination.ganzhi === matched?.ganzhi)
  );
  const chengguWeightRows = [
    ['year', 'yearWeight'],
    ['month', 'monthWeight'],
    ['day', 'dayWeight'],
    ['hour', 'hourWeight']
  ].map(([key, label]) => ({
    key,
    label: t(label),
    item: chenggu?.weights?.[key]
  })).filter((row) => row.item);

  const Defs = ({ rows }) => (
    <div className="fc-defs">
      {rows.filter(([, value]) => value || value === 0).map(([label, value]) => (
        <div className="fc-def" key={label}>
          <span className="fc-def__key">{label}</span>
          <span className="fc-def__val">{asText(value, lang)}</span>
        </div>
      ))}
    </div>
  );

  const SourcePair = ({ classical, plain }) => (
    <div className="bz-sourcepair">
      {classical && (
        <div>
          <span className="fc-eyebrow">{t('classicalText')}</span>
          <p className="bz-poem">{asText(classical, lang)}</p>
        </div>
      )}
      {plain && (
        <div>
          <span className="fc-eyebrow">{t('plainText')}</span>
          <p>{asText(plain, lang)}</p>
        </div>
      )}
    </div>
  );

  const ModernBlock = ({ title, children }) => (
    <section className="bz-block">
      <div className="fc-card bz-modern-card">
        <h3 className="bz-modern-title">{title}</h3>
        {children}
      </div>
    </section>
  );

  const ModernBullets = ({ items }) => {
    const cleanItems = items.map((item) => asText(item, lang)).filter(Boolean);
    if (!cleanItems.length) return null;
    return (
      <ul className="bz-modern-list">
        {cleanItems.map((item, index) => <li key={index}>{item}</li>)}
      </ul>
    );
  };

  const PillarsView = ({ modern = false }) => (
    <section className="bz-pillars-block fc-rise">
      <h2 className="bz-section-title">{modern ? t('modernTitle') : t('zipingTitle')}</h2>
      <p className="bz-intro__desc">
        {modern ? t('modernDesc') : asText(ziping?.method?.plain, lang) || t('zipingMethod')}
      </p>
      <div className="bz-pillars">
        {PILLARS.map(([key, label]) => (
          <div className="bz-pillar" key={key}>
            <span className="bz-pillar__label">{t(label)}</span>
            <span className="bz-pillar__glyphs">
              <span className="bz-pillar__stem">{bazi.stems[key]}</span>
              <span className="bz-pillar__branch">{bazi.branches[key]}</span>
            </span>
            <span className="bz-pillar__nayin">{bazi.ten_gods?.[key]}</span>
          </div>
        ))}
      </div>
      <div className="bz-facts">
        <span className="fc-tag fc-tag--accent">{t('dayMaster')} {asText(ziping?.day_master?.stem || bazi.basic_info?.day_master, lang)}</span>
        {ziping?.strength?.label && <span className="fc-tag">{t('strength')} {asText(ziping.strength.label, lang)}</span>}
        {ziping?.five_elements?.dominant && <span className="fc-tag">{t('dominantElement')} {asText(ziping.five_elements.dominant, lang)}</span>}
        {bazi.basic_info?.day_empty && <span className="fc-tag">{t('dayEmpty')} {bazi.basic_info.day_empty}</span>}
      </div>
    </section>
  );

  const usefulElements = asArray(ziping?.strength?.useful_elements, lang);
  const cautionElements = asArray(ziping?.strength?.caution_elements, lang);
  const scoreElements = (elements = [], tenGod = '') => {
    let score = 52 + (TEN_GOD_SCORE[tenGod] || 0);
    elements.forEach((element) => {
      if (usefulElements.includes(element)) score += 13;
      if (cautionElements.includes(element)) score -= 11;
    });
    return clamp(score, 22, 88);
  };
  const toneForScore = (score) => {
    if (score >= 64) return t('prosperous');
    if (score <= 42) return t('caution');
    return t('steady');
  };
  const reasonForElements = (elements = []) => {
    const helpful = elements.filter((element) => usefulElements.includes(element));
    const careful = elements.filter((element) => cautionElements.includes(element));
    if (helpful.length) {
      return fillTemplate(t('annualUsefulReason'), { elements: helpful.join('、') });
    }
    if (careful.length) {
      return fillTemplate(t('annualCautionReason'), { elements: careful.join('、') });
    }
    return t('annualSteadyReason');
  };

  const currentYear = new Date().getFullYear();
  const annualFortunes = data.annual_fortunes?.years?.length
    ? data.annual_fortunes.years.map((annual) => ({
      year: annual.year,
      ganzhi: annual.pillar,
      score: annual.score,
      tone: annual.tone,
      reason: annual.summary || annual.overall,
      position: annual.position,
      relations: annual.relations
    }))
    : Array.from({ length: 3 }, (_, index) => {
      const year = currentYear + index - 1;
      const annual = getAnnualGanZhi(year);
      const score = scoreElements(annual.elements);
      return {
        year,
        ...annual,
        score,
        tone: toneForScore(score),
        reason: reasonForElements(annual.elements),
        position: index === 0 ? 'previous' : index === 1 ? 'current' : 'next',
        relations: []
      };
    });

  const luckRows = (ziping?.luck_pillars || []).map((pillar) => {
    const stem = pillar.pillar?.slice(0, 1);
    const branch = pillar.pillar?.slice(1, 2);
    const elements = [STEM_ELEMENTS[stem], BRANCH_ELEMENTS[branch]].filter(Boolean);
    const score = scoreElements(elements, pillar.ten_god);
    return {
      ...pillar,
      elements,
      score,
      tone: toneForScore(score)
    };
  });
  const practicalAdvice = buildPracticalAdvice({ usefulElements, cautionElements, luckRows, lang });

  const explainLuckPillar = async (pillar, key) => {
    setLuckLoading((current) => ({ ...current, [key]: true }));
    setLuckErrors((current) => ({ ...current, [key]: '' }));
    try {
      const response = await api.post('/chat', {
        message: buildTenYearPrompt({ pillar, lang, t }),
        bazi_context: {
          ...data,
          selected_luck_pillar: pillar
        },
        chat_history: [],
        lang
      });
      const content = localizeChineseText(response.data?.response || '', lang)
        .replace(LUCK_DISCLAIMER_PATTERN, '')
        .trim();
      setLuckExplanations((current) => ({ ...current, [key]: content }));
    } catch (error) {
      console.error(error);
      setLuckErrors((current) => ({ ...current, [key]: t('luckExplainError') }));
    } finally {
      setLuckLoading((current) => ({ ...current, [key]: false }));
    }
  };

  const luckChartData = {
    labels: luckRows.map((pillar) => pillar.age_range),
    datasets: [
      {
        label: t('fortuneScore'),
        data: luckRows.map((pillar) => pillar.score),
        borderColor: '#b86a12',
        backgroundColor: 'rgba(184, 106, 18, 0.16)',
        pointBackgroundColor: '#b86a12',
        pointBorderColor: '#fffaf0',
        pointRadius: 4,
        borderWidth: 2,
        tension: 0.35,
        fill: true
      }
    ]
  };

  const luckChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${t('fortuneScore')}：${context.parsed.y}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#8a857d' }
      },
      y: {
        min: 20,
        max: 90,
        ticks: { color: '#8a857d' },
        grid: { color: 'rgba(138, 133, 125, 0.18)' }
      }
    }
  };

  const fateRows = [
    [t('modernFamilyTitle'), fillTemplate(t('modernFamilyCopy'), practicalAdvice), [liangtoujian?.predictions?.['基業'], liangtoujian?.predictions?.['兄弟']]],
    [t('modernWealthTitle'), fillTemplate(t('modernWealthCopy'), practicalAdvice), [liangtoujian?.predictions?.['收成'], ziping?.strength?.plain]],
    [t('modernMarriageTitle'), fillTemplate(t('modernMarriageCopy'), practicalAdvice), [liangtoujian?.predictions?.['婚姻']]],
    [t('modernCareerTitle'), fillTemplate(t('modernCareerCopy'), practicalAdvice), [liangtoujian?.predictions?.['行藏'], matched?.interpretation]],
    [t('modernHealthTitle'), fillTemplate(t('modernHealthCopy'), practicalAdvice), [ziping?.health_symbolism?.plain]]
  ];

  const originalReading = (
    <div className="bz-tabpanel" role="tabpanel" id="bazi-tab-original" aria-labelledby="bazi-tab-original-button">
      <PillarsView />

      {ziping && (
        <>
          <section className="bz-block">
            <h3 className="bz-section-title">{t('methodBasis')}</h3>
            <div className="fc-card">
              <SourcePair classical={ziping.method?.classical} plain={ziping.method?.plain} />
            </div>
          </section>

          <section className="bz-block">
            <h3 className="bz-section-title">{t('dayMasterAndMonth')}</h3>
            <div className="bz-grid-2">
              <div className="fc-card">
                <p className="bz-headline">
                  {asText(ziping.day_master?.yin_yang, lang)}{asText(ziping.day_master?.element, lang)}{asText(ziping.day_master?.stem, lang)}
                </p>
                <SourcePair classical={ziping.day_master?.classical} plain={ziping.day_master?.plain} />
              </div>
              <div className="fc-card">
                <p className="bz-headline">{t('monthCommand')} {asText(ziping.month_command?.branch, lang)}</p>
                <Defs rows={[
                  [t('element'), ziping.month_command?.element],
                  [t('hiddenStems'), ziping.month_command?.hidden_stems]
                ]} />
                <SourcePair classical={ziping.month_command?.classical} plain={ziping.month_command?.plain} />
              </div>
            </div>
          </section>

          <section className="bz-block">
            <h3 className="bz-section-title">{t('tenGodsTitle')}</h3>
            <div className="bz-grid-4">
              {PILLARS.map(([key, label]) => {
                const god = ziping.ten_gods?.[key];
                return (
                  <div className="fc-card" key={key}>
                    <span className="fc-eyebrow">{t(label)}</span>
                    <p className="bz-headline">{asText(god?.stem, lang)} {asText(god?.name, lang)}</p>
                    <SourcePair classical={god?.classical} plain={god?.plain} />
                  </div>
                );
              })}
            </div>
          </section>

          <section className="bz-block">
            <h3 className="bz-section-title">{t('hiddenStemsTitle')}</h3>
            <div className="bz-grid-4">
              {PILLARS.map(([key, label]) => {
                const hidden = ziping.hidden_stems?.[key];
                return (
                  <div className="fc-card" key={key}>
                    <span className="fc-eyebrow">{t(label)} {asText(hidden?.branch, lang)}</span>
                    <p className="bz-headline">{hiddenStemHeadline(hidden, lang)}</p>
                    <SourcePair classical={hidden?.classical} plain={hidden?.plain} />
                  </div>
                );
              })}
            </div>
          </section>

          <section className="bz-block">
            <h3 className="bz-section-title">{t('fiveElementsTitle')}</h3>
            <div className="fc-card">
              <div className="bz-element-bars">
                {ELEMENT_KEYS.map(([element, key]) => {
                  const value = ziping.five_elements?.counts?.[element] || 0;
                  return (
                    <div className="bz-element" key={element}>
                      <span className="bz-element__label">{element}</span>
                      <span className="bz-element__track">
                        <span className="bz-element__fill" style={{ width: `${Math.min(value * 8, 100)}%` }} />
                      </span>
                      <span className="bz-element__value">{value || bazi.wuxing?.[key] || 0}</span>
                    </div>
                  );
                })}
              </div>
              <div className="bz-callout bz-callout--accent">
                <Defs rows={[
                  [t('dominantElement'), ziping.five_elements?.dominant],
                  [t('missingElements'), ziping.five_elements?.missing],
                  [t('usefulElements'), ziping.strength?.useful_elements],
                  [t('cautionElements'), ziping.strength?.caution_elements],
                  [t('strengthScore'), ziping.strength?.score]
                ]} />
              </div>
              <SourcePair classical={ziping.five_elements?.classical} plain={ziping.five_elements?.plain} />
              <SourcePair classical={ziping.strength?.classical} plain={ziping.strength?.plain} />
            </div>
          </section>

          <section className="bz-block">
            <h3 className="bz-section-title">{t('personalityHealthTitle')}</h3>
            <div className="bz-grid-2">
              <div className="fc-card">
                <SourcePair classical={ziping.personality?.classical} plain={ziping.personality?.plain} />
              </div>
              <div className="fc-card">
                <SourcePair classical={ziping.health_symbolism?.classical} plain={ziping.health_symbolism?.plain} />
                {ziping.health_symbolism?.disclaimer && <p className="bz-note">{asText(ziping.health_symbolism.disclaimer, lang)}</p>}
              </div>
            </div>
          </section>

          {ziping.luck_pillars?.length > 0 && (
            <section className="bz-block">
              <h3 className="bz-section-title">{t('luckPillars')}</h3>
              <div className="bz-luck">
                {ziping.luck_pillars.map((pillar) => (
                  <div className="bz-luck__item" key={`${pillar.pillar}-${pillar.age_range}`}>
                    <span className="bz-luck__glyph">{pillar.pillar}</span>
                    <span className="bz-luck__range">{pillar.age_range}{t('age')}</span>
                    <span className="bz-luck__year">{pillar.ten_god}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {liangtoujian && (
        <section className="bz-block">
          <h3 className="bz-section-title">{t('liangtoujianTitle')}</h3>
          <div className="fc-card">
            <div className="bz-facts">
              <span className="fc-tag fc-tag--accent">{t('symbolHeads')} {bazi.stems.year}＋{bazi.stems.hour}＝{asText(liangtoujian.liangtoujian, lang)}</span>
              {matched && <span className="fc-tag">{t('matchingKey')} {asText(matched.ganzhi, lang)}</span>}
              {liangtoujian.gua && <span className="fc-tag">{t('gua')} {asText(liangtoujian.gua, lang)}</span>}
            </div>
            <p className="bz-headline">{asText(matched?.title, lang)}</p>
            <div className="bz-callout bz-callout--accent">
              <Defs rows={[
                [t('star'), matchingStar?.name],
                [t('ganZhi'), matched?.ganzhi],
                [t('explanation'), matched?.interpretation]
              ]} />
            </div>

            {liangtoujian.stars?.length > 0 && (
              <div className="bz-sub">
                <span className="fc-eyebrow">{t('starCombinations')}</span>
                <div className="bz-star-grid">
                  {liangtoujian.stars.map((star, starIndex) => (
                    <article className="bz-star-card" key={`${asText(star.name, lang)}-${starIndex}`}>
                      <h4>{asText(star.name, lang)}</h4>
                      <div className="bz-combo-list">
                        {star.combinations?.map((combo, comboIndex) => {
                          const isMatched = asText(combo.ganzhi, lang) === asText(matched?.ganzhi, lang);
                          return (
                            <div
                              className={`bz-combo ${isMatched ? 'bz-combo--matched' : ''}`}
                              key={`${asText(combo.ganzhi, lang)}-${comboIndex}`}
                            >
                              <div className="bz-combo__head">
                                <span className="bz-combo__ganzhi">{asText(combo.ganzhi, lang)}</span>
                                <span className="bz-combo__title">{asText(combo.title, lang)}</span>
                                {isMatched && <span className="fc-tag fc-tag--accent">{t('selectedLifeGate')}</span>}
                              </div>
                              <p>{asText(combo.interpretation, lang)}</p>
                            </div>
                          );
                        })}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}

            {liangtoujian.judgement && (
              <div className="bz-sub">
                <span className="fc-eyebrow">{t('judgement')}</span>
                <p>{asText(liangtoujian.judgement, lang)}</p>
              </div>
            )}

            {liangtoujian.pattern && (
              <div className="bz-sub">
                <Defs rows={[[t('pattern'), liangtoujian.pattern.name]]} />
                {liangtoujian.pattern.poems?.map((poem, index) => <p className="bz-poem" key={index}>{asText(poem, lang)}</p>)}
              </div>
            )}

            {liangtoujian.predictions && Object.keys(liangtoujian.predictions).length > 0 && (
              <div className="bz-sub">
                <span className="fc-eyebrow">{t('predictions')}</span>
                <div className="bz-prediction-grid">
                  {Object.entries(liangtoujian.predictions).map(([label, value]) => (
                    <article className="bz-prediction-card" key={label}>
                      <h4>{label}</h4>
                      <p>{asText(value, lang)}</p>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {chenggu && (
        <section className="bz-block">
          <h3 className="bz-section-title">{t('chengguTitle')}</h3>
          <div className="fc-card">
            <p className="bz-intro__desc">{t('chengguMethod')}</p>
            <div className="bz-facts">
              <span className="fc-tag fc-tag--accent">{t('totalBoneWeight')} {asText(chenggu.total_display, lang)}</span>
              <span className="fc-tag">
                {t('birthLunarDate')} {asText(chenggu.lunar?.year_ganzhi, lang)}{t('year')} {asText(chenggu.lunar?.month_label, lang)}{asText(chenggu.lunar?.day_label, lang)} {asText(chenggu.lunar?.hour_branch, lang)}{t('hour')}
              </span>
              <span className="fc-tag">{t('gender')} {asText(chenggu.gender, lang)}</span>
            </div>
            <div className="bz-weight-grid">
              {chengguWeightRows.map((row) => (
                <article className="bz-weight-card" key={row.key}>
                  <span className="fc-eyebrow">{row.label}</span>
                  <strong>{asText(row.item.label, lang)}</strong>
                  <span>{asText(row.item.display, lang)}</span>
                </article>
              ))}
            </div>
            <div className="bz-sub">
              <span className="fc-eyebrow">{t('boneSong')} · {asText(chenggu.song?.label, lang)}</span>
              <p className="bz-poem">{asText(chenggu.song?.verse, lang)}</p>
            </div>
            {chenggu.song?.annotation && (
              <div className="bz-sub">
                <span className="fc-eyebrow">{t('boneAnnotation')}</span>
                <p>{asText(chenggu.song.annotation, lang)}</p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );

  const modernReading = (
    <div className="bz-tabpanel" role="tabpanel" id="bazi-tab-modern" aria-labelledby="bazi-tab-modern-button">
      <PillarsView modern />

      <ModernBlock title={t('modernLifeCourseTitle')}>
        <p>
          {fillTemplate(t('modernLifeCourseCopy'), {
            dayMaster: asText(ziping?.day_master?.stem || bazi.basic_info?.day_master, lang),
            month: asText(ziping?.month_command?.branch, lang),
            strength: asText(ziping?.strength?.label, lang),
            personality: compactText(ziping?.personality?.plain, lang, 90) || t('none'),
            useful: asText(ziping?.strength?.useful_elements, lang) || t('none'),
            caution: asText(ziping?.strength?.caution_elements, lang) || t('none')
          })}
        </p>
        <div className="bz-grid-2 bz-modern-mini">
          <Defs rows={[
            [t('yearPillar'), bazi.pillars?.year],
            [t('monthPillar'), bazi.pillars?.month],
            [t('dayMaster'), ziping?.day_master?.stem || bazi.basic_info?.day_master],
            [t('strength'), ziping?.strength?.label]
          ]} />
          <Defs rows={[
            [t('usefulElements'), ziping?.strength?.useful_elements],
            [t('cautionElements'), ziping?.strength?.caution_elements],
            [t('dominantElement'), ziping?.five_elements?.dominant],
            [t('missingElements'), ziping?.five_elements?.missing]
          ]} />
        </div>
        <div className="bz-practical-grid">
          {[
            [practicalAdvice.labels?.occupation, practicalAdvice.occupation],
            [practicalAdvice.labels?.area, practicalAdvice.area],
            [practicalAdvice.labels?.action, practicalAdvice.action],
            [practicalAdvice.labels?.avoid, practicalAdvice.avoid]
          ].map(([label, value], index) => {
            return (
              <article className="bz-practical-card" key={label || index}>
                <span className="fc-eyebrow">{label}</span>
                <p>{value}</p>
              </article>
            );
          })}
        </div>
        <p className="bz-practical-note">{practicalAdvice.reason}</p>
        <ModernBullets items={[
          ziping?.day_master?.plain,
          ziping?.month_command?.plain,
          matched?.interpretation
        ]} />
      </ModernBlock>

      <ModernBlock title={t('modernYearlyTitle')}>
        <p>{t('annualFortuneBrief')}</p>
        <div className="bz-year-grid">
          {annualFortunes.map((year) => (
            <article className="bz-year-card" key={year.year}>
              <span className="bz-year-card__year">{year.year}</span>
              <span className="bz-year-card__ganzhi">{year.ganzhi}</span>
              <span className={`fc-tag ${year.score >= 64 ? 'fc-tag--accent' : ''}`}>{year.tone} · {year.score}</span>
              <p>
                {fillTemplate(t('yearlyNote'), {
                  year: year.year,
                  ganzhi: year.ganzhi,
                  tone: year.tone,
                  reason: year.reason || (year.relations?.length ? year.relations.join('、') : '')
                })}
              </p>
            </article>
          ))}
        </div>
        <div className="bz-inline-action">
          <a className="fc-btn fc-btn--secondary" href="/bazi/annual/">{t('viewAnnualDetails')}</a>
        </div>
      </ModernBlock>

      {chenggu && (
        <ModernBlock title={t('modernChengguTitle')}>
          <p>
            {fillTemplate(t('modernChengguCopy'), {
              weight: asText(chenggu.total_display, lang),
              label: asText(chenggu.song?.label, lang)
            })}
          </p>
          <div className="bz-callout bz-callout--accent">
            <Defs rows={[
              [t('birthLunarDate'), `${asText(chenggu.lunar?.year_ganzhi, lang)}${t('year')} ${asText(chenggu.lunar?.month_label, lang)}${asText(chenggu.lunar?.day_label, lang)} ${asText(chenggu.lunar?.hour_branch, lang)}${t('hour')}`],
              [t('totalBoneWeight'), asText(chenggu.total_display, lang)],
              [t('boneSong'), chenggu.song?.verse],
              [t('boneAnnotation'), chenggu.song?.annotation]
            ]} />
          </div>
        </ModernBlock>
      )}

      {luckRows.length > 0 && (
        <ModernBlock title={t('modernTenYearTitle')}>
          <p>
            {fillTemplate(t('modernTenYearCopy'), {
              startAge: `${bazi.luck_pillars?.start_age || ziping?.luck_pillars?.[0]?.age_range || ''}${t('age')}`
            })}
          </p>
          <div className="bz-chart bz-chart--line">
            <Line data={luckChartData} options={luckChartOptions} />
          </div>
          <div className="bz-luck bz-luck--detailed">
            {luckRows.map((pillar, index) => {
              const key = `${pillar.pillar}-${pillar.age_range}-${index}`;
              return (
              <div className="bz-luck__item" key={key}>
                <span className="bz-luck__glyph">{pillar.pillar}</span>
                <span className="bz-luck__range">{pillar.age_range}{t('age')}</span>
                <span className="bz-luck__year">{pillar.ten_god} · {pillar.tone} · {pillar.score}</span>
                <p className="bz-luck__plain">
                  {fillTemplate(t('luckPillarNote'), {
                    range: pillar.age_range,
                    pillar: pillar.pillar,
                    tenGod: pillar.ten_god,
                    plain: compactText(cleanLuckText(pillar.plain, lang), lang, 110)
                  })}
                </p>
                <button
                  type="button"
                  className="fc-btn fc-btn--secondary fc-btn--sm bz-luck__explain-btn"
                  onClick={() => explainLuckPillar(pillar, key)}
                  disabled={!!luckLoading[key]}
                >
                  {luckLoading[key] ? (
                    <>
                      <span className="bz-loading-dot" aria-hidden="true" />
                      {t('luckExplainLoading')}
                    </>
                  ) : t('luckExplainMore')}
                </button>
                {luckErrors[key] && <p className="bz-luck__error">{luckErrors[key]}</p>}
                {luckExplanations[key] && (
                  <div className="bz-luck-explanation">
                    <ReactMarkdown>{luckExplanations[key]}</ReactMarkdown>
                  </div>
                )}
              </div>
            );})}
          </div>
        </ModernBlock>
      )}

      <ModernBlock title={t('modernFateTitle')}>
        <div className="bz-fate-grid">
          {fateRows.map(([title, copy, items]) => (
            <article className="bz-fate-card" key={title}>
              <h4>{title}</h4>
              <p>{copy}</p>
              <ModernBullets items={items.map((item) => compactText(item, lang, 120))} />
            </article>
          ))}
        </div>
      </ModernBlock>
    </div>
  );

  return (
    <div className="bz-resume">
      <div className="bz-tabs tabs tabs-border" role="tablist" aria-label={t('readingTabsLabel')}>
        <button
          type="button"
          id="bazi-tab-original-button"
          className="bz-tab tab"
          role="tab"
          aria-selected={activeTab === 'original'}
          aria-controls="bazi-tab-original"
          onClick={() => setActiveTab('original')}
        >
          {t('tabOriginal')}
        </button>
        <button
          type="button"
          id="bazi-tab-modern-button"
          className="bz-tab tab"
          role="tab"
          aria-selected={activeTab === 'modern'}
          aria-controls="bazi-tab-modern"
          onClick={() => setActiveTab('modern')}
        >
          {t('tabModern')}
        </button>
      </div>
      {activeTab === 'original' ? originalReading : modernReading}
    </div>
  );
};

export default FortuneResume;
